import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ChevronRight, Download, FolderInput, FolderPlus, FolderUp, LayoutGrid, List, Lock, Shield, Trash2, Upload, UserPlus,
} from 'lucide-react';
import driveApi, { ROLES } from '../api/drive';
import { useDrive } from '../contexts/DriveProvider';
import ItemsView, { useSelection } from '../components/drive/ItemsView';
import DetailsPanel from '../components/drive/DetailsPanel';
import ShareDialog from '../components/drive/ShareDialog';
import useItemActions from '../components/drive/useItemActions';
import { filesFromDrop, isFileDrag } from '../components/drive/dropFiles';
import PageHeader, { PageTitle } from '../components/drive/PageHeader';
import { DRIVE_EVENTS } from '../components/drive/events';
import { ContextMenu, ContextMenuContent, ContextMenuEntries, ContextMenuLabel, ContextMenuTrigger } from '../components/ui/context-menu';

const readView = () => {
  try { return localStorage.getItem('drive.view') || 'list'; } catch { return 'list'; }
};

export const ToolbarButton = ({ icon: Icon, children, primary, ...props }) => (
  <button
    type="button"
    className={`flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:opacity-50 ${
      primary ? 'bg-primary px-3 text-primary-foreground hover:bg-primary/85' : 'text-foreground hover:bg-muted'
    }`}
    {...props}
  >
    <Icon className="h-4 w-4" aria-hidden="true" />
    <span className="hidden sm:inline">{children}</span>
  </button>
);

export const ViewToggle = ({ view, setView }) => (
  <div role="group" aria-label="View" className="ml-1 flex rounded-lg border border-border p-0.5">
    {[['list', List, 'List view'], ['grid', LayoutGrid, 'Grid view']].map(([key, Icon, label]) => (
      <button
        key={key}
        type="button"
        aria-label={label}
        aria-pressed={view === key}
        onClick={() => {
          setView(key);
          try { localStorage.setItem('drive.view', key); } catch { /* storage blocked */ }
        }}
        className={`flex h-[26px] w-7 items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${view === key ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
      >
        <Icon className="h-3.5 w-3.5" />
      </button>
    ))}
  </div>
);

const FolderPage = () => {
  const { driveId, folderId } = useParams();
  const { version, setLocation, uploadFiles, toast } = useDrive();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [view, setView] = useState(readView);
  const [dragging, setDragging] = useState(false);
  const [sharingDrive, setSharingDrive] = useState(null);
  const actions = useItemActions();

  useEffect(() => {
    let cancelled = false;
    setError(null);
    driveApi.contents(driveId, folderId)
      .then((res) => { if (!cancelled) setData(res); })
      .catch((err) => { if (!cancelled) setError(err?.response?.status === 404 ? 'notfound' : 'failed'); });
    return () => { cancelled = true; };
  }, [driveId, folderId, version]);

  // Reset when navigating to another folder so stale rows never flash
  useEffect(() => { setData(null); }, [driveId, folderId]);

  useEffect(() => {
    if (data) setLocation({ driveId, folderId: folderId || null, role: data.role });
    return () => setLocation(null);
  }, [data, driveId, folderId, setLocation]);

  const items = data?.items || [];
  const selection = useSelection(items);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.closest('input, textarea, select, [role="dialog"]')) return;
      if (e.key === 'Escape') selection.clear();
      if ((e.key === 'Delete' || e.key === 'Backspace') && selection.selectedItems.length) {
        const editable = selection.selectedItems.filter((i) => i.role >= ROLES.EDITOR);
        if (editable.length) actions.trash(editable);
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        selection.selectAll();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (error) {
    return (
      <div className="p-10 text-center">
        <h1 className="text-lg font-semibold">{error === 'notfound' ? 'This folder isn’t available' : 'Could not load this folder'}</h1>
        <p className="mt-2 text-muted-foreground">
          {error === 'notfound' ? 'It may have been moved, deleted, or you no longer have access.' : 'Check your connection and try again.'}
        </p>
      </div>
    );
  }

  const canEdit = data && data.role >= ROLES.EDITOR;
  // Breadcrumbs accept dragged items too: the way to move something up a level by mouse
  const crumbDrop = (id, name) => ({
    onDragOver: (e) => {
      if (e.dataTransfer.types.includes('application/x-drive-nodes')) {
        e.preventDefault();
        e.currentTarget.dataset.dropping = 'true';
      }
    },
    onDragLeave: (e) => { delete e.currentTarget.dataset.dropping; },
    onDrop: (e) => {
      delete e.currentTarget.dataset.dropping;
      const raw = e.dataTransfer.getData('application/x-drive-nodes');
      if (!raw) return;
      e.preventDefault();
      e.stopPropagation();
      actions.moveInto(JSON.parse(raw), { id, name, drive: driveId });
    },
  });

  const selected = selection.selectedItems;
  const focus = selected.length === 1 ? selected[0] : (selected.length ? null : data?.folder);
  const isDriveRoot = data && !data.folder;
  // A super admin opening someone else's My Drive (from Storage & usage)
  const driveName = data?.owner ? `${data.owner.name}’s My Drive` : data?.drive?.name;
  const title = data?.folder?.name || driveName || '';

  const onDragOver = (e) => {
    if (!canEdit || !isFileDrag(e)) return;
    e.preventDefault();
    setDragging(true);
  };
  const onDrop = async (e) => {
    if (!canEdit || !isFileDrag(e)) return;
    e.preventDefault();
    setDragging(false);
    const files = await filesFromDrop(e.dataTransfer);
    if (files.length) uploadFiles(files, { drive: driveId, parent: folderId || null });
  };

  const downloadTargets = selected.length ? selected : (data?.folder ? [data.folder] : items);
  const share = () => {
    if (selected.length === 1) actions.share(selected[0]);
    else if (data.folder) actions.share(data.folder);
    else if (data.drive.kind === 'shared') setSharingDrive(data.drive);
    else toast('Select a file or folder to share it.');
  };

  // Right-click on empty space: create/upload here, or act on the folder itself
  const fire = (name) => () => window.dispatchEvent(new Event(name));
  const backgroundEntries = [];
  if (canEdit) {
    backgroundEntries.push(
      { label: 'New folder', icon: FolderPlus, run: fire(DRIVE_EVENTS.newFolder) },
      { label: 'Upload files', icon: Upload, run: fire(DRIVE_EVENTS.uploadFiles) },
      { label: 'Upload folder', icon: FolderUp, run: fire(DRIVE_EVENTS.uploadFolder) },
    );
  }
  if (data && (data.folder || data.drive.kind === 'shared')) {
    if (backgroundEntries.length) backgroundEntries.push('-');
    backgroundEntries.push({ label: data.folder ? 'Share this folder' : 'Share this drive', icon: UserPlus, run: share });
    if (data.folder) backgroundEntries.push({ label: 'Download this folder (.zip)', icon: Download, run: () => actions.downloadAll([data.folder], data.folder.name) });
    if (data.folder && data.role >= ROLES.MANAGER) backgroundEntries.push({ label: 'Folder security', icon: Shield, run: () => actions.secure(data.folder) });
  }

  return (
    <div
      className="relative flex min-h-screen flex-col"
      onDragOver={onDragOver}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false); }}
      onDrop={onDrop}
    >
      <PageHeader
        actions={(
          <>
            {selected.length > 0 && canEdit && selected.every((i) => i.role >= ROLES.EDITOR) && (
              <ToolbarButton icon={FolderInput} onClick={() => actions.moveTo(selected)}>Move</ToolbarButton>
            )}
            {selected.length > 0 && canEdit && (
              <ToolbarButton icon={Trash2} onClick={() => actions.trash(selected.filter((i) => i.role >= ROLES.EDITOR))}>Trash</ToolbarButton>
            )}
            {data?.folder && data.role >= ROLES.MANAGER && selected.length === 0 && (
              <ToolbarButton icon={Shield} onClick={() => actions.secure(data.folder)}>Security</ToolbarButton>
            )}
            <ToolbarButton
              icon={Download}
              disabled={!downloadTargets.length}
              onClick={() => (selected.length ? actions.download(downloadTargets) : actions.downloadAll(downloadTargets, title))}
            >
              {selected.length > 1 ? `Download ${selected.length}` : selected.length ? 'Download' : 'Download all'}
            </ToolbarButton>
            <ToolbarButton icon={UserPlus} primary disabled={!data} onClick={share}>Share</ToolbarButton>
            <ViewToggle view={view} setView={setView} />
          </>
        )}
      >
        <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1 text-[14px] text-muted-foreground">
          {data && !isDriveRoot && data.reaches_root && (
            <>
              <Link to={`/drive/${driveId}`} {...crumbDrop(null, driveName)} className="shrink-0 truncate rounded px-1 data-[dropping=true]:bg-accent data-[dropping=true]:outline data-[dropping=true]:outline-2 data-[dropping=true]:outline-filament hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{driveName}</Link>
              <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            </>
          )}
          {data?.breadcrumbs.slice(0, -1).map((c) => (
            <React.Fragment key={c.id}>
              <Link to={`/drive/${driveId}/folder/${c.id}`} {...crumbDrop(c.id, c.name)} className="hidden truncate rounded px-1 data-[dropping=true]:bg-accent data-[dropping=true]:outline data-[dropping=true]:outline-2 data-[dropping=true]:outline-filament hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:block">{c.name}</Link>
              <ChevronRight className="hidden h-3.5 w-3.5 shrink-0 sm:block" aria-hidden="true" />
            </React.Fragment>
          ))}
          <span className="min-w-0 px-1 text-foreground" aria-current="page"><PageTitle>{title || ' '}</PageTitle></span>
        </nav>
        {data?.folder?.restricted && (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 text-xs font-medium">
            <Lock className="h-3 w-3" aria-hidden="true" /> Restricted
          </span>
        )}
      </PageHeader>
      {data?.owner && (
        <p role="note" className="mx-4 mt-3 rounded-lg bg-muted px-3 py-2 text-[13px] sm:mx-6 lg:mx-8">
          You’re viewing {data.owner.email}’s My Drive as a super admin. Opening it is recorded in the activity log.
        </p>
      )}

      <div className="flex flex-1 flex-col lg:flex-row">
        <ContextMenu>
        <ContextMenuTrigger asChild disabled={!backgroundEntries.length}>
        <section
          aria-label="Files"
          className="flex min-w-0 flex-1 flex-col gap-4 px-4 py-4 sm:px-6"
          onContextMenu={() => selection.clear()}
        >
          {!data && (
            <div className="flex flex-col gap-1.5" aria-busy="true">
              {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-10 animate-pulse rounded-md bg-muted" />)}
            </div>
          )}
          {data && items.length > 0 && (
            <ItemsView
              view={view}
              items={items}
              selection={selection}
              actions={actions}
              onMoveInto={canEdit ? actions.moveInto : undefined}
            />
          )}
          {data && items.length === 0 && (
            <div className="mx-auto mt-16 flex max-w-sm flex-col items-center gap-2 text-center">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-border">
                <Upload className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
              </span>
              <p className="mt-1 font-semibold">This folder is empty</p>
              <p className="text-[13.5px] text-muted-foreground">{canEdit ? 'Drop files or folders here, or press New.' : 'Nothing has been added here yet.'}</p>
            </div>
          )}
          {canEdit && items.length > 0 && (
            <p className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
              <Upload className="h-3.5 w-3.5" aria-hidden="true" /> Drop files or folders anywhere to upload here — or right-click for more
            </p>
          )}
        </section>
        </ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuLabel>{title}</ContextMenuLabel>
          <ContextMenuEntries entries={backgroundEntries} />
        </ContextMenuContent>
        </ContextMenu>
        <div className="border-t border-border px-4 py-5 sm:px-6 lg:sticky lg:top-14 lg:h-[calc(100vh-3.5rem)] lg:w-[320px] lg:shrink-0 lg:overflow-y-auto lg:border-l lg:border-t-0 lg:px-5">
          {data && <DetailsPanel item={focus} onShare={actions.share} selectionCount={selected.length} />}
        </div>
      </div>

      {dragging && (
        <div className="pointer-events-none absolute inset-2 z-30 flex items-center justify-center rounded-xl border-2 border-dashed border-filament bg-accent/85">
          <p className="font-semibold text-accent-foreground">Drop to upload to “{title}”</p>
        </div>
      )}

      {actions.dialogs}
      <ShareDialog drive={sharingDrive} onOpenChange={(o) => !o && setSharingDrive(null)} />
    </div>
  );
};

export default FolderPage;
